import { Link, useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Menu,
  User,
  LogOut,
  LayoutDashboard,
  Award,
  CalendarCheck,
} from "lucide-react";
import { useState, useEffect } from "react";
import { useUserProfiles } from "@/hooks/useUserProfiles";
import { useAuth } from "@/components/auth/AuthProvider";
import { AuthDialog } from "@/components/auth/AuthDialog";
import { GTranslateWidget } from "@/components/GTranslateWidget";
import { ProfessionalSearchBar } from "@/components/search";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@/components/ui/navigation-menu";
import { Separator } from "@/components/ui/separator";
import logo from "@/assets/ekselencia-logo-main.png";
import { useNavigationContext } from "@/hooks/useNavigationContext";
import NavDropdownMenu from "@/components/navigation/NavDropdownMenu";
import StickySecondaryNav from "@/components/navigation/StickySecondaryNav";
import { cn } from "@/lib/utils";
import { DescubrimientoCaracteristicas } from "@/components/ayuda";

const Navigation = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const { hasAnyProfile, primaryProfile } = useUserProfiles();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [authDialogOpen, setAuthDialogOpen] = useState(false);

  // Use context-aware navigation
  const { context, menuConfig, contextId } = useNavigationContext();

  const isActive = (path: string) => location.pathname === path;

  const handleCtaClick = () => {
    if (menuConfig.cta.action === 'auth') {
      setAuthDialogOpen(true);
    } else if (menuConfig.cta.path) {
      navigate(menuConfig.cta.path);
    }
  };

  return (
    <>
      {/* GTranslate - Only loads when user clicks the translate button */}
      <GTranslateWidget />

      {/* Sticky Secondary Nav - appears on scroll */}
      <StickySecondaryNav onAuthClick={() => setAuthDialogOpen(true)} />

      <nav className="fixed top-9 left-0 right-0 z-50 bg-gradient-to-r from-primary to-accent backdrop-blur-lg border-b border-white/20 shadow-xl" style={{ contain: 'layout style' }}>
        <div className="container mx-auto px-4">
          {/* First Row - Logo, Search Bar, User Actions */}
          <div className="flex items-center gap-4 h-16">
            {/* Logo - Fixed dimensions to prevent CLS */}
            <Link to="/" className="flex items-center flex-shrink-0" style={{ width: '150px', height: '69px' }}>
              <img src={logo} alt="Ekselencia" className="h-[69px] w-[150px] object-contain" width="150" height="69" />
            </Link>

            {/* Big Search Bar - Desktop with predictive search */}
            <div className="hidden lg:flex flex-1 max-w-2xl mx-auto">
              <ProfessionalSearchBar 
                variant="navbar"
                placeholder="Buscar gimnasios, entrenadores, eventos, tribus..."
                showVoice={true}
                showSuggestions={true}
                className="w-full [&_input]:h-12 [&_input]:bg-white/10 [&_input]:border-2 [&_input]:border-white/30 [&_input]:text-white [&_input]:placeholder:text-white/60 [&_input]:focus:bg-white/20 [&_input]:focus:border-white/50 [&_input]:rounded-full"
              />
            </div>

            <div className="hidden lg:flex items-center space-x-2 flex-shrink-0">
              {/* Feature Discovery */}
              <DescubrimientoCaracteristicas className="mr-2" />
              {user ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="nav" size="sm" className="gap-2">
                      <User className="w-4 h-4" />
                      Mi Cuenta
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="bg-popover">
                    <DropdownMenuItem onClick={() => navigate("/dashboard")}>
                      <LayoutDashboard className="w-4 h-4 mr-2" />
                      Dashboard
                    </DropdownMenuItem>
                    {hasAnyProfile && primaryProfile && (
                      <DropdownMenuItem onClick={() => navigate(primaryProfile.dashboardPath)}>
                        <CalendarCheck className="w-4 h-4 mr-2" />
                        Sistema de Reservas
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem onClick={() => navigate("/affiliate/dashboard")}>
                      <Award className="w-4 h-4 mr-2" />
                      Panel de Afiliados
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={signOut}>
                      <LogOut className="w-4 h-4 mr-2" />
                      Cerrar Sesión
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <Button variant="nav" size="sm" onClick={() => setAuthDialogOpen(true)}>
                  Iniciar Sesión
                </Button>
              )}
              <Button 
                size="sm"
                variant="navActive"
                onClick={handleCtaClick}
              >
                {menuConfig.cta.label}
              </Button>
            </div>

            <AuthDialog open={authDialogOpen} onOpenChange={setAuthDialogOpen} />

            {/* Mobile Menu Sheet */}
            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="lg:hidden text-white hover:bg-white/10 ml-auto"
                  aria-label="Toggle menu"
                >
                  <Menu className="w-6 h-6" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[300px] bg-gradient-to-b from-primary to-accent border-l border-white/20 overflow-y-auto">
                <SheetHeader>
                  <SheetTitle className="text-white text-left flex items-center gap-2">
                    <context.icon className="w-4 h-4" />
                    {context.label}
                  </SheetTitle>
                </SheetHeader>

                {/* Mobile Search with predictive search */}
                <div className="mt-6 mb-6">
                  <ProfessionalSearchBar 
                    variant="navbar"
                    placeholder="Buscar..."
                    showVoice={true}
                    showSuggestions={true}
                    className="w-full [&_input]:bg-white/10 [&_input]:border-2 [&_input]:border-white/30 [&_input]:text-white [&_input]:placeholder:text-white/60 [&_input]:focus:bg-white/20 [&_input]:focus:border-white/50 [&_input]:rounded-lg"
                  />
                </div>

                {/* Navigation Section - Context-aware */}
                <div className="space-y-1 mb-6">
                  <p className="text-xs font-semibold text-white/60 uppercase tracking-wider px-2 mb-2">
                    {context.label}
                  </p>
                  {menuConfig.menuItems.map((item) => (
                    <Link
                      key={item.path + item.label}
                      to={item.path}
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <Button
                        variant="ghost"
                        className={cn(
                          "w-full justify-start text-white hover:bg-white/10",
                          isActive(item.path) && "bg-white/20 font-semibold",
                          item.special && "bg-destructive hover:bg-destructive/90 text-white font-bold"
                        )}
                      >
                        <item.icon className="w-4 h-4 mr-3" />
                        {item.label}
                      </Button>
                    </Link>
                  ))}
                </div>

                <Separator className="bg-white/20 my-4" />

                {/* User Actions Section */}
                <div className="space-y-1">
                  <p className="text-xs font-semibold text-white/60 uppercase tracking-wider px-2 mb-2">Cuenta</p>
                  {user ? (
                    <>
                      <Button 
                        variant="ghost" 
                        className="w-full justify-start text-white hover:bg-white/10"
                        onClick={() => {
                          navigate("/dashboard");
                          setMobileMenuOpen(false);
                        }}
                      >
                        <LayoutDashboard className="w-4 h-4 mr-3" />
                        Dashboard
                      </Button>
                      {hasAnyProfile && primaryProfile && (
                        <Button 
                          variant="ghost" 
                          className="w-full justify-start text-white hover:bg-white/10"
                          onClick={() => {
                            navigate(primaryProfile.dashboardPath);
                            setMobileMenuOpen(false);
                          }}
                        >
                          <CalendarCheck className="w-4 h-4 mr-3" />
                          Sistema de Reservas
                        </Button>
                      )}
                      <Button 
                        variant="ghost" 
                        className="w-full justify-start text-white hover:bg-white/10"
                        onClick={() => {
                          navigate("/affiliate/dashboard");
                          setMobileMenuOpen(false);
                        }}
                      >
                        <Award className="w-4 h-4 mr-3" />
                        Panel de Afiliados
                      </Button>
                      <Button 
                        variant="ghost" 
                        className="w-full justify-start text-white hover:bg-white/10"
                        onClick={() => {
                          signOut();
                          setMobileMenuOpen(false);
                        }}
                      >
                        <LogOut className="w-4 h-4 mr-3" />
                        Cerrar Sesión
                      </Button>
                    </>
                  ) : (
                    <Button 
                      variant="ghost" 
                      className="w-full justify-start text-white hover:bg-white/10"
                      onClick={() => {
                        setAuthDialogOpen(true);
                        setMobileMenuOpen(false);
                      }}
                    >
                      <User className="w-4 h-4 mr-3" />
                      Iniciar Sesión
                    </Button>
                  )}
                  
                  <Separator className="bg-white/20 my-4" />
                  
                  <Button 
                    className="w-full bg-white text-primary hover:bg-white/90 font-semibold"
                    onClick={() => {
                      handleCtaClick();
                      setMobileMenuOpen(false);
                    }}
                  >
                    {menuConfig.cta.label}
                  </Button>
                </div>
              </SheetContent>
            </Sheet>
          </div>

          {/* Second Row - Context-aware Navigation with Dropdowns (Desktop Only) */}
          <div className="hidden lg:flex items-center justify-center py-2 border-t border-white/20">
            {/* Context Label */}
            {contextId !== 'home' && (
              <span className="text-xs text-white/60 uppercase tracking-wider mr-4 flex items-center gap-1">
                <context.icon className="w-3 h-3" />
                {context.label}
                <span className="text-white/40">›</span>
              </span>
            )}

            {/* Navigation Menu with Dropdowns */}
            <NavigationMenu>
              <NavigationMenuList className="gap-1">
                {menuConfig.menuItems.map((item) => (
                  item.hasDropdown && item.dropdownId && context.dropdowns[item.dropdownId] ? (
                    <NavigationMenuItem key={item.path + item.label}>
                      <NavigationMenuTrigger
                        className={cn(
                          "h-8 px-3 text-xs bg-transparent text-white/90 hover:bg-white/10 hover:text-white data-[state=open]:bg-white/20",
                          "focus:bg-white/10 focus:text-white"
                        )}
                      >
                        <item.icon className="w-3 h-3 mr-1.5" />
                        {item.label}
                      </NavigationMenuTrigger>
                      <NavigationMenuContent>
                        <NavDropdownMenu config={context.dropdowns[item.dropdownId]} />
                      </NavigationMenuContent>
                    </NavigationMenuItem>
                  ) : (
                    <NavigationMenuItem key={item.path + item.label}>
                      <Link to={item.path}>
                        <Button
                          variant={isActive(item.path) ? "navActive" : "nav"}
                          size="sm"
                          className={cn(
                            "h-8 text-xs",
                            item.special && "bg-destructive text-destructive-foreground hover:bg-destructive/90 border-none"
                          )}
                        >
                          <item.icon className="w-3 h-3 mr-1.5" />
                          {item.label}
                        </Button>
                      </Link>
                    </NavigationMenuItem>
                  )
                ))}
              </NavigationMenuList>
            </NavigationMenu>
          </div>
        </div>
      </nav>
    </>
  );
};

export default Navigation;
